import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Form, Spinner } from "react-bootstrap";

import { FORGOT_USER_PASSWORD_CLEAR_SUCCESS } from "../../../constants/userConstants";

import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPasswordAdmin,
  resetPasswordAdmin,
  clearErrors,
} from "../../../actions/reduxActions/userActions";
import { useTranslation } from "react-i18next";
import { usePageMeta } from "../../../lib/usePageMeta";
import "../AuthPage.scss";

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const history = useMemo(
    () => ({
      push: (to) => navigate(to),
      replace: (to) => navigate(to, { replace: true }),
      goBack: () => navigate(-1),
    }),
    [navigate]
  );

  const [t, i18n] = useTranslation("common");
  const isRtl = i18n.languages[0] === "urdu";
  const rtl = isRtl ? "text-end" : "";

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const [passwordResetCode, setPasswordResetCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const dispatch = useDispatch();

  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { error, loading, message, forgotSuccess, success } = useSelector(
    (state) => state.forgotPassword
  );

  // If an authenticated user lands here, bounce them home.
  useEffect(() => {
    if (user && isAuthenticated === true) {
      navigate("/");
    }
  }, [user, isAuthenticated, history, navigate]);

  // Auto-clear errors so a stale message doesn't stick around forever.
  useEffect(() => {
    if (error) {
      const id = setTimeout(() => dispatch(clearErrors()), 8000);
      return () => clearTimeout(id);
    }
  }, [error, dispatch]);

  // Move to step 2 once the request-code call returns a success message.
  useEffect(() => {
    if (message && forgotSuccess) {
      setSent(true);
      const id = setTimeout(() => {
        dispatch({ type: FORGOT_USER_PASSWORD_CLEAR_SUCCESS });
      }, 16000);
      return () => clearTimeout(id);
    }
  }, [message, forgotSuccess, dispatch]);

  // After successful reset, redirect to admin login.
  useEffect(() => {
    if (success) {
      toast.success(`${t("resetPass.updated")}`);
      dispatch({ type: FORGOT_USER_PASSWORD_CLEAR_SUCCESS });
      navigate("/pk/secure/admin/login");
    }
  }, [success, history, dispatch, t, navigate]);

  const handleRequestCode = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.set("email", email);
    dispatch(forgotPasswordAdmin(formData));
  };

  const handleReset = (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return toast.error(t("resetPass.mismatch"));
    }

    const fd = new FormData();
    fd.set("passwordResetCode", passwordResetCode);
    fd.set("password", password);
    fd.set("confirmPassword", confirmPassword);
    dispatch(resetPasswordAdmin(fd));
  };

  usePageMeta({
    title: "PkPlots Admin | Forgot Password",
    description: "Reset your PkPlots admin password",
  });

  if (user) return null;

  return (
    <div className={`auth-page ${isRtl ? "rtl" : ""}`}>
      <div className="auth-page__card">
        <div className={`auth-page__head ${rtl}`}>
          <h1 className="auth-page__title">
            {sent ? t("resetPass.reset_pass") : t("forgotPass.forgot_pass")}
          </h1>
          <p className="auth-page__subtitle">
            {sent ? t("resetPass.subtitle") : t("forgotPass.subtitle")}
          </p>
        </div>

        {message && forgotSuccess && (
          <div className="auth-page__alert auth-page__alert--success">
            <i className="bx bx-check-circle" aria-hidden="true"></i>
            <span>{t(message)}</span>
          </div>
        )}
        {error && (
          <div className="auth-page__alert auth-page__alert--danger">
            <i className="bx bx-error-circle" aria-hidden="true"></i>
            <span>{t(error)}</span>
          </div>
        )}

        {sent ? (
          <Form onSubmit={handleReset} noValidate>
            <Form.Group className={`auth-page__group ${rtl}`} controlId="adminForgot-passwordResetCode">
              <Form.Label>{t("resetPass.reset_code")}</Form.Label>
              <div className="auth-page__field">
                <Form.Control
                  name="passwordResetCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={passwordResetCode}
                  onChange={(e) => setPasswordResetCode(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </Form.Group>

            <Form.Group className={`auth-page__group ${rtl}`} controlId="adminForgot-password">
              <Form.Label>{t("resetPass.new_pass")}</Form.Label>
              <div className="auth-page__field auth-page__field--password">
                <Form.Control
                  name="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="toggle"
                  onClick={() => setShowPass((s) => !s)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  <i
                    className={showPass ? "bx bx-hide" : "bx bx-show"}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </Form.Group>

            <Form.Group className={`auth-page__group ${rtl}`} controlId="adminForgot-confirmPassword">
              <Form.Label>{t("resetPass.confirm_pass")}</Form.Label>
              <div className="auth-page__field auth-page__field--password">
                <Form.Control
                  name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="toggle"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  <i
                    className={showConfirm ? "bx bx-hide" : "bx bx-show"}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </Form.Group>

            <button
              type="submit"
              className="auth-page__submit"
              disabled={!!loading}
            >
              {loading ? (
                <Spinner as="span" animation="border" size="sm" role="status" />
              ) : (
                <i className="bx bx-check" aria-hidden="true"></i>
              )}
              <span>{t("resetPass.reset_pass")}</span>
            </button>

            <div className="auth-page__foot">
              <button
                type="button"
                className="linklike"
                onClick={() => setSent(false)}
              >
                {t("resetPass.no_code")}
              </button>
            </div>
          </Form>
        ) : (
          <Form onSubmit={handleRequestCode} noValidate>
            <Form.Group className={`auth-page__group ${rtl}`} controlId="adminForgot-email">
              <Form.Label>{t("forgotPass.email")}</Form.Label>
              <div className="auth-page__field">
                <Form.Control
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder={t("forgotPass.type_your")}
                />
              </div>
            </Form.Group>

            <button
              disabled={!email || !!loading}
              type="submit"
              className="auth-page__submit"
            >
              {loading ? (
                <Spinner as="span" animation="border" size="sm" role="status" />
              ) : (
                <i className="bx bx-mail-send" aria-hidden="true"></i>
              )}
              <span>{t("forgotPass.send")}</span>
            </button>

            <div className="auth-page__foot">
              <button
                type="button"
                className="linklike"
                onClick={() => setSent(true)}
              >
                {t("forgotPass.have_code")}
              </button>
            </div>
          </Form>
        )}

        <div className="auth-page__foot">
          <Link to="/pk/secure/admin/login" className="auth-page__back">
            <i className="bx bx-arrow-back" aria-hidden="true"></i>
            <span>{t("forgotPass.back_to_login")}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPassword;
